'use client'

import {
  Code2,
  Users,
  Layers,
  Eye,
  Brain,
  Database,
  Zap,
  Shield,
  SlidersHorizontal,
  Activity,
  RotateCcw,
  ArrowUpRight,
  Monitor,
  Bot,
  Wrench,
  CheckCircle2,
  ArrowRight,
  HeartHandshake,
  AlertTriangle,
  RefreshCw,
  Lock,
  Unlock,
  UserCheck,
  ShieldCheck,
  HardDrive,
  Clock,
  BookOpen,
  Waypoints,
  Network,
  CircleDot,
  type LucideIcon,
} from 'lucide-react'

const brandColors = {
  primary: '#008dcc',
  accent: '#FDC304',
  pink: '#F90B8A',
  purple: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
}

/**
 * Shows the two parallel conversations around AX:
 * Developer AX (Biilmann/Netlify) vs Human AX (Pixelmojo's focus)
 */
export function TwoThreadsDiagram() {
  const threads = [
    {
      label: 'Developer AX',
      subtitle: 'Making products usable BY agents',
      color: brandColors.purple,
      icon: Code2,
      champions: ['Netlify (Biilmann)', 'Nordic APIs', 'Platform teams'],
      focus: [
        'API design for agent consumption',
        'llms.txt and MCP tool manifests',
        'Authentication and permissions',
        'Context window optimization',
      ],
      question: 'Can an AI agent operate my product?',
    },
    {
      label: 'Human AX',
      subtitle: 'Designing experiences WITH agents',
      color: brandColors.pink,
      icon: Users,
      champions: ['Pixelmojo', 'Salesforce', 'Smashing Magazine'],
      focus: [
        'Trust and transparency patterns',
        'Autonomy controls for users',
        'Agent personality and voice',
        'Failure recovery and escalation',
      ],
      question: 'What does the human experience feel like?',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          Two Definitions of AX
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Same acronym, different audiences, both essential
        </p>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {threads.map(thread => (
            <div
              key={thread.label}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-center gap-3 mb-3'>
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{
                    backgroundColor: `${thread.color}15`,
                    color: thread.color,
                  }}
                >
                  <thread.icon className='w-5 h-5' />
                </div>
                <div>
                  <span className='text-sm font-semibold text-foreground'>
                    {thread.label}
                  </span>
                  <p className='text-xs text-muted-foreground'>
                    {thread.subtitle}
                  </p>
                </div>
              </div>
              <p
                className='text-xs font-medium mb-3 italic'
                style={{ color: thread.color }}
              >
                &ldquo;{thread.question}&rdquo;
              </p>
              <div className='space-y-1.5 mb-3'>
                {thread.focus.map(item => (
                  <div key={item} className='flex items-start gap-2'>
                    <CheckCircle2
                      className='w-3 h-3 mt-0.5 flex-shrink-0'
                      style={{ color: thread.color }}
                    />
                    <span className='text-xs text-muted-foreground'>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className='flex flex-wrap gap-1.5'>
                {thread.champions.map(name => (
                  <span
                    key={name}
                    className='text-xs font-mono px-2 py-0.5 rounded'
                    style={{
                      backgroundColor: `${thread.color}15`,
                      color: thread.color,
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className='mt-4 p-3 rounded-lg bg-muted text-center'>
          <p className='text-xs text-muted-foreground'>
            A complete AX strategy needs both. This series focuses on the{' '}
            <span style={{ color: brandColors.pink, fontWeight: 600 }}>
              human side
            </span>{' '}
            because that is where the design discipline is being reinvented.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The AX Design Framework: Three Layers
 * Capabilities > Trust Patterns > Protocol Stack
 */
export function AXFrameworkDiagram() {
  const layers = [
    {
      name: 'Capabilities Layer',
      subtitle: 'What the agent can do',
      color: brandColors.primary,
      icon: Layers,
      items: [
        { icon: Eye, label: 'Perception', detail: 'What the agent sees' },
        { icon: Brain, label: 'Reasoning', detail: 'How it thinks' },
        { icon: Database, label: 'Memory', detail: 'What it remembers' },
        { icon: Zap, label: 'Agency', detail: 'What it does' },
      ],
    },
    {
      name: 'Trust Layer',
      subtitle: 'How humans stay in control',
      color: brandColors.pink,
      icon: Shield,
      items: [
        {
          icon: Eye,
          label: 'Intent Preview',
          detail: 'See before it acts',
        },
        {
          icon: SlidersHorizontal,
          label: 'Autonomy Dial',
          detail: 'Set boundaries',
        },
        {
          icon: Activity,
          label: 'Confidence Signal',
          detail: 'Know its certainty',
        },
        {
          icon: RotateCcw,
          label: 'Action Audit',
          detail: 'Undo any action',
        },
      ],
    },
    {
      name: 'Protocol Layer',
      subtitle: 'How the system connects',
      color: brandColors.accent,
      icon: Monitor,
      items: [
        {
          icon: Monitor,
          label: 'AG-UI',
          detail: 'Agent to frontend',
        },
        {
          icon: Bot,
          label: 'A2A',
          detail: 'Agent to agent',
        },
        {
          icon: Wrench,
          label: 'MCP',
          detail: 'Agent to tools',
        },
        {
          icon: ArrowUpRight,
          label: 'A2UI',
          detail: 'Agent to UI catalog',
        },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The AX Design Framework
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Three layers that every agentic experience must address
        </p>
        <div className='space-y-4'>
          {layers.map((layer, i) => (
            <div key={layer.name}>
              <div className='bg-background border border-border/60 rounded-xl p-4'>
                <div className='flex items-center gap-3 mb-3'>
                  <div
                    className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{
                      backgroundColor: `${layer.color}15`,
                      color: layer.color,
                    }}
                  >
                    <layer.icon className='w-5 h-5' />
                  </div>
                  <div>
                    <div className='flex items-center gap-2'>
                      <span
                        className='text-xs font-mono px-2 py-0.5 rounded-full'
                        style={{
                          backgroundColor: `${layer.color}15`,
                          color: layer.color,
                        }}
                      >
                        Layer {i + 1}
                      </span>
                      <span className='text-sm font-semibold text-foreground'>
                        {layer.name}
                      </span>
                    </div>
                    <p className='text-xs text-muted-foreground mt-0.5'>
                      {layer.subtitle}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                  {layer.items.map(item => (
                    <div key={item.label} className='rounded-lg p-2 bg-muted'>
                      <div className='flex items-center gap-1.5 mb-1'>
                        <item.icon
                          className='w-3.5 h-3.5'
                          style={{ color: layer.color }}
                        />
                        <span className='text-xs font-medium text-foreground'>
                          {item.label}
                        </span>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        {item.detail}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              {i < layers.length - 1 && (
                <div className='flex justify-center py-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * AX vs UX: What changes when agents enter the picture
 */
export function AXvsUXDiagram() {
  const comparisons = [
    {
      dimension: 'Primary user',
      ux: 'Human operates the interface',
      ax: 'Human and agent share the interface',
    },
    {
      dimension: 'Design artifact',
      ux: 'Wireframes, user flows, prototypes',
      ax: 'Trust policies, autonomy maps, escalation paths',
    },
    {
      dimension: 'Success metric',
      ux: 'Task completion time',
      ax: 'Appropriate delegation rate',
    },
    {
      dimension: 'Error handling',
      ux: 'Error messages with retry',
      ax: 'Agent explains what went wrong and proposes fix',
    },
    {
      dimension: 'Personalization',
      ux: 'User settings and preferences',
      ax: 'Agent learns and adapts over sessions',
    },
    {
      dimension: 'Designer role',
      ux: 'Interface architect',
      ax: 'Experience orchestrator',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          What Changes from UX to AX
        </h3>
        <div className='space-y-3'>
          {comparisons.map(row => (
            <div
              key={row.dimension}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <p className='text-sm font-medium text-foreground mb-3'>
                {row.dimension}
              </p>
              <div className='grid grid-cols-2 gap-3'>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.purple}08`,
                    borderLeft: `3px solid ${brandColors.purple}40`,
                  }}
                >
                  <span
                    className='text-xs font-medium'
                    style={{ color: brandColors.purple }}
                  >
                    Traditional UX
                  </span>
                  <p className='text-xs text-muted-foreground mt-1'>{row.ux}</p>
                </div>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.pink}08`,
                    borderLeft: `3px solid ${brandColors.pink}40`,
                  }}
                >
                  <span
                    className='text-xs font-medium'
                    style={{ color: brandColors.pink }}
                  >
                    AX Design
                  </span>
                  <p className='text-xs text-muted-foreground mt-1'>{row.ax}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * AX Maturity Model: Where most companies are today
 * Anchored on verified 2026 data: ~79% have adopted agents in some form,
 * only 11% run them in production (S&P Global / Deloitte enterprise surveys).
 */
export function AXMaturityDiagram() {
  const stages = [
    {
      level: 1,
      name: 'Not Adopted',
      percentage: 21,
      color: brandColors.red,
      description: 'No AI agents and no agentic strategy yet.',
    },
    {
      level: 2,
      name: 'Adopted, Not Live',
      percentage: 68,
      color: brandColors.accent,
      description:
        'Exploring or piloting agents, but nothing in production for users.',
    },
    {
      level: 3,
      name: 'In Production',
      percentage: 11,
      color: brandColors.green,
      description:
        'Agents live with governance and trust patterns. Where the standard gets set.',
    },
  ]

  // Calculate cumulative for bar positioning
  let cumulative = 0
  const stagesWithPosition = stages.map(s => {
    const start = cumulative
    cumulative += s.percentage
    return { ...s, start }
  })

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          AX Maturity: Where Companies Are Today
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Based on S&P Global and Deloitte 2026 enterprise surveys
        </p>

        {/* Stacked bar */}
        <div className='h-6 rounded-full overflow-hidden flex mb-4'>
          {stagesWithPosition.map(s => (
            <div
              key={s.name}
              className='h-full flex items-center justify-center'
              style={{
                width: `${s.percentage}%`,
                backgroundColor: s.color,
              }}
            >
              <span className='text-xs font-mono text-white font-bold'>
                {s.percentage}%
              </span>
            </div>
          ))}
        </div>

        {/* Legend cards */}
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
          {stages.map(s => (
            <div
              key={s.name}
              className='rounded-lg p-3'
              style={{
                backgroundColor: `${s.color}08`,
                borderLeft: `3px solid ${s.color}`,
              }}
            >
              <div className='flex items-center gap-2 mb-1'>
                <span
                  className='text-xs font-mono px-1.5 py-0.5 rounded'
                  style={{
                    backgroundColor: `${s.color}15`,
                    color: s.color,
                  }}
                >
                  L{s.level}
                </span>
                <span className='text-xs font-semibold text-foreground'>
                  {s.name}
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>{s.description}</p>
            </div>
          ))}
        </div>
        <p className='text-xs text-muted-foreground mt-4 text-center'>
          Only 11% of companies run agents in production, while roughly 79% have
          adopted them in some form. The design patterns you adopt now will
          define the standard.
        </p>
      </div>
    </div>
  )
}

// ============================================================
// Part 2: Trust Design Patterns
// ============================================================

/**
 * Trust Progression: How trust builds over time from stranger to partner
 */
export function TrustProgressionDiagram() {
  const phases = [
    {
      phase: 'Week 1',
      name: 'Cautious Observer',
      icon: Eye,
      color: brandColors.red,
      autonomy: 'Manual',
      behavior: 'User reviews every action. Agent suggests, human decides.',
      trigger: 'First interaction',
    },
    {
      phase: 'Weeks 2-4',
      name: 'Supervised Assistant',
      icon: UserCheck,
      color: brandColors.accent,
      autonomy: 'Supervised',
      behavior:
        'User approves batches. Agent acts on routine tasks after confirmation.',
      trigger: 'Agent handles 10+ tasks without error',
    },
    {
      phase: 'Months 2-3',
      name: 'Trusted Coworker',
      icon: HeartHandshake,
      color: brandColors.primary,
      autonomy: 'Bounded',
      behavior:
        'Agent acts within agreed boundaries. User reviews exceptions only.',
      trigger: 'User adjusts autonomy dial upward',
    },
    {
      phase: 'Month 4+',
      name: 'Autonomous Partner',
      icon: ShieldCheck,
      color: brandColors.green,
      autonomy: 'Full',
      behavior:
        'Agent handles domain independently. Reports outcomes, not requests.',
      trigger: 'Consistent performance across edge cases',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          How Trust Builds Over Time
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          The four phases of human-agent trust progression
        </p>
        <div className='space-y-4'>
          {phases.map((p, i) => (
            <div key={p.name}>
              <div className='bg-background border border-border/60 rounded-xl p-4'>
                <div className='flex items-start gap-3'>
                  <div
                    className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{
                      backgroundColor: `${p.color}15`,
                      color: p.color,
                    }}
                  >
                    <p.icon className='w-5 h-5' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap mb-1'>
                      <span
                        className='text-xs font-mono px-2 py-0.5 rounded-full'
                        style={{
                          backgroundColor: `${p.color}15`,
                          color: p.color,
                        }}
                      >
                        {p.phase}
                      </span>
                      <span className='text-sm font-semibold text-foreground'>
                        {p.name}
                      </span>
                      <span className='text-xs font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground'>
                        {p.autonomy}
                      </span>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {p.behavior}
                    </p>
                    <p className='text-xs mt-1' style={{ color: p.color }}>
                      Unlocked by: {p.trigger}
                    </p>
                  </div>
                </div>
              </div>
              {i < phases.length - 1 && (
                <div className='flex justify-center py-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Trust Breakdown and Recovery: What happens when trust breaks
 */
export function TrustBreakdownDiagram() {
  const stages = [
    {
      icon: AlertTriangle,
      label: 'Trust Violation',
      detail: 'Agent makes a visible mistake or takes an unexpected action',
      color: brandColors.red,
    },
    {
      icon: Eye,
      label: 'Transparency Response',
      detail:
        'Agent immediately explains what happened, why, and what data it used',
      color: brandColors.accent,
    },
    {
      icon: RotateCcw,
      label: 'Recovery Action',
      detail:
        'One-click undo restores previous state. Agent proposes corrective action.',
      color: brandColors.primary,
    },
    {
      icon: SlidersHorizontal,
      label: 'Autonomy Reset',
      detail:
        'System automatically reduces autonomy level. User re-establishes boundaries.',
      color: brandColors.purple,
    },
    {
      icon: RefreshCw,
      label: 'Gradual Rebuild',
      detail:
        'Trust rebuilds through demonstrated reliability. Each error-free interaction restores confidence.',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Trust Recovery Cycle
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          What happens when an agent makes a mistake (and how to recover)
        </p>
        <div className='space-y-4'>
          {stages.map((step, i) => (
            <div key={step.label}>
              <div className='flex items-start gap-4'>
                <div
                  className='flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center'
                  style={{
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                  }}
                >
                  <step.icon className='w-5 h-5' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-xs font-mono px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${step.color}15`,
                        color: step.color,
                      }}
                    >
                      Step {i + 1}
                    </span>
                  </div>
                  <p className='text-foreground font-medium mt-1'>
                    {step.label}
                  </p>
                  <p className='text-sm text-muted-foreground'>{step.detail}</p>
                </div>
              </div>
              {i < stages.length - 1 && (
                <div className='flex justify-center py-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Progressive Autonomy Levels: What the user controls at each level
 */
export function ProgressiveAutonomyDiagram() {
  const levels = [
    {
      level: 1,
      name: 'Full Manual',
      icon: Lock,
      color: brandColors.red,
      userDoes: 'Makes all decisions, agent only suggests',
      agentDoes: 'Researches, drafts, recommends',
      approvalNeeded: 'Every action',
      example: 'Agent drafts email, user reviews and sends',
    },
    {
      level: 2,
      name: 'Supervised',
      icon: UserCheck,
      color: brandColors.accent,
      userDoes: 'Approves before execution, sets task priorities',
      agentDoes: 'Plans and prepares, queues actions for review',
      approvalNeeded: 'Before each batch',
      example: 'Agent schedules 5 meetings, user confirms all at once',
    },
    {
      level: 3,
      name: 'Bounded',
      icon: Shield,
      color: brandColors.primary,
      userDoes: 'Sets rules and limits, reviews exceptions',
      agentDoes: 'Acts within boundaries, escalates edge cases',
      approvalNeeded: 'Only for exceptions',
      example: 'Agent responds to support tickets under $100 value',
    },
    {
      level: 4,
      name: 'Full Autonomy',
      icon: Unlock,
      color: brandColors.green,
      userDoes: 'Reviews outcomes periodically, adjusts goals',
      agentDoes: 'Operates independently, reports results',
      approvalNeeded: 'None (audit trail available)',
      example: 'Agent manages lead qualification pipeline end-to-end',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Four Autonomy Levels
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          What changes at each level of the autonomy dial
        </p>
        <div className='space-y-3'>
          {levels.map(l => (
            <div
              key={l.name}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-center gap-3 mb-3'>
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{
                    backgroundColor: `${l.color}15`,
                    color: l.color,
                  }}
                >
                  <l.icon className='w-5 h-5' />
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-xs font-mono px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${l.color}15`,
                        color: l.color,
                      }}
                    >
                      Level {l.level}
                    </span>
                    <span className='text-sm font-semibold text-foreground'>
                      {l.name}
                    </span>
                  </div>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-2 mb-2'>
                <div className='rounded-lg p-2 bg-muted'>
                  <span className='text-xs font-medium text-foreground'>
                    Human does
                  </span>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {l.userDoes}
                  </p>
                </div>
                <div className='rounded-lg p-2 bg-muted'>
                  <span className='text-xs font-medium text-foreground'>
                    Agent does
                  </span>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {l.agentDoes}
                  </p>
                </div>
              </div>
              <p className='text-xs text-muted-foreground italic'>
                Example: {l.example}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Trust Data: Key statistics about the trust gap
 */
export function TrustGapDataDiagram() {
  const stats = [
    {
      stat: '6%',
      label: 'fully trust AI agents for core processes',
      source: 'HBR, 603 leaders surveyed',
      color: brandColors.red,
    },
    {
      stat: '43%→27%',
      label: 'trust in autonomous agents (dropped in one year)',
      source: 'Capgemini, 1,500 executives',
      color: brandColors.accent,
    },
    {
      stat: '32%',
      label: 'of designers trust AI output',
      source: 'Figma 2025 AI Report',
      color: brandColors.purple,
    },
    {
      stat: '63%',
      label: 'say human oversight would build their trust',
      source: 'Salesforce Trust Research',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Trust Gap in Numbers
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Why trust design patterns are not optional
        </p>
        <div className='grid grid-cols-2 gap-3'>
          {stats.map(s => (
            <div
              key={s.label}
              className='bg-background border border-border/60 rounded-xl p-4 text-center'
            >
              <p
                className='text-2xl font-bold font-mono'
                style={{ color: s.color }}
              >
                {s.stat}
              </p>
              <p className='text-xs text-muted-foreground mt-1'>{s.label}</p>
              <p className='text-xs font-mono mt-2' style={{ color: s.color }}>
                {s.source}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Part 3: Conversation Flow Architecture Diagrams
// ============================================================

/**
 * Multi-turn degradation stats
 */
export function MultiTurnDegradationDiagram() {
  const stats = [
    {
      stat: '39%',
      label: 'average performance drop in multi-turn vs single-turn',
      source: 'Microsoft Research + Salesforce, 2025',
      color: brandColors.red,
    },
    {
      stat: '9.21',
      label: 'average turns before failure mode triggers',
      source: 'Psychology Today, Feb 2026',
      color: brandColors.accent,
    },
    {
      stat: '40%+',
      label: 'of agentic AI projects will be canceled by 2027',
      source: 'Gartner, June 2025',
      color: brandColors.pink,
    },
    {
      stat: '1m 38s',
      label: 'average chatbot-only conversation length',
      source: 'Fullview, 2025',
      color: brandColors.purple,
    },
  ]

  const failureModes = [
    {
      name: 'Premature Assumption',
      description: 'Agent jumps to conclusions from partial context',
      icon: Zap,
      color: brandColors.red,
    },
    {
      name: 'Prior Response Anchoring',
      description: 'Agent over-relies on its own previous answers',
      icon: RotateCcw,
      color: brandColors.accent,
    },
    {
      name: 'Correction Failure',
      description: 'Agent cannot integrate user corrections',
      icon: AlertTriangle,
      color: brandColors.pink,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Multi-Turn Problem in Numbers
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Why conversation architecture is not optional
        </p>
        <div className='grid grid-cols-2 gap-3 mb-6'>
          {stats.map(s => (
            <div
              key={s.label}
              className='bg-background border border-border/60 rounded-xl p-4 text-center'
            >
              <p
                className='text-2xl font-bold font-mono'
                style={{ color: s.color }}
              >
                {s.stat}
              </p>
              <p className='text-xs text-muted-foreground mt-1'>{s.label}</p>
              <p className='text-xs font-mono mt-2' style={{ color: s.color }}>
                {s.source}
              </p>
            </div>
          ))}
        </div>

        <h4 className='text-sm font-semibold text-foreground mb-3'>
          The Three Failure Modes
        </h4>
        <div className='space-y-2'>
          {failureModes.map(mode => {
            const Icon = mode.icon
            return (
              <div
                key={mode.name}
                className='flex items-start gap-3 bg-background border border-border/60 rounded-lg p-3'
              >
                <div
                  className='w-8 h-8 rounded-lg flex items-center justify-center shrink-0'
                  style={{ backgroundColor: `${mode.color}15` }}
                >
                  <Icon className='w-4 h-4' style={{ color: mode.color }} />
                </div>
                <div>
                  <p className='text-sm font-medium text-foreground'>
                    {mode.name}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {mode.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Conversation state machine: Perception → Reasoning → Action → Observation loop
 */
export function ConversationStateMachineDiagram() {
  const phases: {
    name: string
    description: string
    icon: LucideIcon
    color: string
    productExample: string
  }[] = [
    {
      name: 'Perception',
      description: 'Interpret user input in full conversation context',
      icon: Eye,
      color: brandColors.primary,
      productExample: 'Salesforce Atlas Reasoning Engine',
    },
    {
      name: 'Reasoning',
      description: 'Plan response using tools, knowledge, and history',
      icon: Brain,
      color: brandColors.purple,
      productExample: 'Copilot Studio Generative Orchestration',
    },
    {
      name: 'Action',
      description: 'Execute the chosen action (gated by trust patterns)',
      icon: Zap,
      color: brandColors.accent,
      productExample: 'Intent Preview from Part 2',
    },
    {
      name: 'Observation',
      description: 'Evaluate result, decide next step or stop condition',
      icon: Activity,
      color: brandColors.green,
      productExample: 'State checkpoint + Action Audit',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Conversation State Machine
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Every production agent follows this loop until a stop condition is met
        </p>

        <div className='space-y-3'>
          {phases.map((phase, i) => {
            const Icon = phase.icon
            return (
              <div key={phase.name}>
                <div className='flex items-start gap-4 bg-background border border-border/60 rounded-xl p-4'>
                  <div className='flex flex-col items-center shrink-0'>
                    <div
                      className='w-10 h-10 rounded-xl flex items-center justify-center'
                      style={{ backgroundColor: `${phase.color}15` }}
                    >
                      <Icon
                        className='w-5 h-5'
                        style={{ color: phase.color }}
                      />
                    </div>
                    <span
                      className='text-xs font-mono mt-1'
                      style={{ color: phase.color }}
                    >
                      {i + 1}
                    </span>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-semibold text-foreground'>
                      {phase.name}
                    </p>
                    <p className='text-xs text-muted-foreground mt-0.5'>
                      {phase.description}
                    </p>
                    <p
                      className='text-xs font-mono mt-1.5'
                      style={{ color: phase.color }}
                    >
                      {phase.productExample}
                    </p>
                  </div>
                </div>
                {i < phases.length - 1 && (
                  <div className='flex justify-center py-1'>
                    <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div
          className='mt-4 rounded-lg p-3 text-center'
          style={{
            backgroundColor: `${brandColors.primary}08`,
            border: `1px solid ${brandColors.primary}20`,
          }}
        >
          <p className='text-xs text-muted-foreground'>
            <span className='font-mono' style={{ color: brandColors.primary }}>
              Loop
            </span>{' '}
            continues until: task complete, user exit, or escalation trigger
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Three memory layers: short-term, long-term, episodic
 */
export function ContextPersistenceDiagram() {
  const layers: {
    name: string
    scope: string
    description: string
    icon: LucideIcon
    color: string
    production: string
    contains: string[]
  }[] = [
    {
      name: 'Short-Term Memory',
      scope: 'Active session',
      description: 'Structured scratchpad for the current conversation',
      icon: Clock,
      color: brandColors.primary,
      production: 'Google Agent Engine Sessions',
      contains: [
        'Current goal',
        'Active entities',
        'Unresolved questions',
        'Rolling summary',
      ],
    },
    {
      name: 'Long-Term Memory',
      scope: 'Cross-session',
      description: 'User profiles and historical patterns that persist',
      icon: HardDrive,
      color: brandColors.purple,
      production: 'Amazon Bedrock AgentCore Memory',
      contains: [
        'User preferences',
        'Trust level',
        'Past decisions',
        'Communication style',
      ],
    },
    {
      name: 'Episodic Memory',
      scope: 'Semantic retrieval',
      description: 'Retrievable past interactions organized by topic',
      icon: BookOpen,
      color: brandColors.accent,
      production: 'Google Memory Bank',
      contains: [
        'Past conversations by topic',
        'Relevant precedents',
        'Historical recommendations',
        'Resolution patterns',
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Three Memory Layers
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Production context persistence requires all three
        </p>

        <div className='space-y-4'>
          {layers.map(layer => {
            const Icon = layer.icon
            return (
              <div
                key={layer.name}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                <div className='flex items-center gap-3 mb-3'>
                  <div
                    className='w-9 h-9 rounded-lg flex items-center justify-center shrink-0'
                    style={{ backgroundColor: `${layer.color}15` }}
                  >
                    <Icon
                      className='w-4.5 h-4.5'
                      style={{ color: layer.color }}
                    />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-foreground'>
                      {layer.name}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {layer.scope}
                    </p>
                  </div>
                </div>
                <p className='text-xs text-foreground/80 mb-3'>
                  {layer.description}
                </p>
                <div className='grid grid-cols-2 gap-1.5 mb-2'>
                  {layer.contains.map(item => (
                    <div
                      key={item}
                      className='bg-muted rounded px-2 py-1 text-xs text-muted-foreground'
                    >
                      {item}
                    </div>
                  ))}
                </div>
                <p
                  className='text-xs font-mono mt-2'
                  style={{ color: layer.color }}
                >
                  {layer.production}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Handoff topologies: hub-and-spoke, mesh, swarm
 */
export function HandoffTopologyDiagram() {
  const topologies: {
    name: string
    description: string
    icon: LucideIcon
    color: string
    strengths: string[]
    platform: string
  }[] = [
    {
      name: 'Hub-and-Spoke',
      description: 'Central orchestrator routes all conversations',
      icon: Waypoints,
      color: brandColors.primary,
      strengths: ['Complete visibility', 'Easy to audit', 'Consistent context'],
      platform: 'Microsoft Copilot Studio',
    },
    {
      name: 'Mesh',
      description: 'Agents communicate directly with peers',
      icon: Network,
      color: brandColors.purple,
      strengths: [
        'No single point of failure',
        'Lower latency',
        'Scales horizontally',
      ],
      platform: 'Anthropic MCP',
    },
    {
      name: 'Swarm',
      description: 'Lightweight peer-to-peer handoffs',
      icon: CircleDot,
      color: brandColors.accent,
      strengths: [
        'Minimal overhead',
        'Fastest handoffs',
        'Simple mental model',
      ],
      platform: 'OpenAI Agents SDK',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          Handoff Topologies
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          How agents transfer conversations to other agents or humans
        </p>

        <div className='space-y-3'>
          {topologies.map(t => {
            const Icon = t.icon
            return (
              <div
                key={t.name}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                <div className='flex items-center gap-3 mb-3'>
                  <div
                    className='w-9 h-9 rounded-lg flex items-center justify-center shrink-0'
                    style={{ backgroundColor: `${t.color}15` }}
                  >
                    <Icon className='w-4.5 h-4.5' style={{ color: t.color }} />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-foreground'>
                      {t.name}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {t.description}
                    </p>
                  </div>
                </div>
                <div className='flex flex-wrap gap-1.5 mb-2'>
                  {t.strengths.map(s => (
                    <span
                      key={s}
                      className='text-xs rounded-full px-2.5 py-0.5'
                      style={{
                        backgroundColor: `${t.color}15`,
                        color: t.color,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <p className='text-xs font-mono' style={{ color: t.color }}>
                  {t.platform}
                </p>
              </div>
            )
          })}
        </div>

        <div
          className='mt-4 rounded-lg p-3'
          style={{
            backgroundColor: `${brandColors.green}08`,
            border: `1px solid ${brandColors.green}20`,
          }}
        >
          <p className='text-xs text-foreground/80'>
            <span className='font-semibold'>The Handoff Contract:</span>{' '}
            Conversation summary + active entities + current goal + trust level
            + action history
          </p>
        </div>
      </div>
    </div>
  )
}
